<template>
  <div class="p-4">
    <!-- Header -->
    <div class="flex justify-between items-center mb-6">
      <div>
        <h1 class="text-2xl font-bold text-gray-800">{{ $t('Mes livreurs') }}</h1>
        <p class="text-gray-500">{{ $t('Suivi des livreurs et de leurs performances') }}</p>
      </div>
      <router-link
        to="/stockist-dashboard"
        class="px-4 py-2 border rounded-lg hover:bg-gray-100"
      >
        {{ $t('Retour') }}
      </router-link>
    </div>

    <!-- Stats Summary -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      <div class="bg-white rounded-lg shadow p-4">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-blue-100 text-blue-600">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"></path>
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-gray-500 text-sm">{{ $t('Total livreurs') }}</p>
            <p class="text-2xl font-semibold text-gray-800">{{ couriers.length }}</p>
          </div>
        </div>
      </div>

      <div class="bg-white rounded-lg shadow p-4">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-green-100 text-green-600">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-gray-500 text-sm">{{ $t('Colis livres (total)') }}</p>
            <p class="text-2xl font-semibold text-gray-800">{{ getTotalDelivered() }}</p>
          </div>
        </div>
      </div>

      <div class="bg-white rounded-lg shadow p-4">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-yellow-100 text-yellow-600">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-gray-500 text-sm">{{ $t('Colis en cours (total)') }}</p>
            <p class="text-2xl font-semibold text-gray-800">{{ getTotalShipped() }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Couriers Grid -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <div v-if="loading" class="col-span-full">
        <div class="bg-white rounded-lg shadow p-8 text-center">
          <svg class="animate-spin h-8 w-8 text-blue-600 mx-auto" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p class="mt-2 text-gray-500">{{ $t('Chargement...') }}</p>
        </div>
      </div>

      <div v-else-if="couriers.length === 0" class="col-span-full">
        <div class="bg-white rounded-lg shadow p-8 text-center">
          <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"></path>
          </svg>
          <p class="mt-4 text-gray-500">{{ $t('Aucun livreur trouve') }}</p>
        </div>
      </div>

      <!-- Courier Card -->
      <div
        v-else
        v-for="courier in couriers"
        :key="courier._id"
        class="bg-white rounded-lg shadow overflow-hidden"
      >
        <div class="p-4 border-b bg-gray-50">
          <div class="flex items-center">
            <div class="h-12 w-12 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold text-lg">
              {{ getInitials(courier.fullName) }}
            </div>
            <div class="ml-3">
              <h4 class="font-semibold text-gray-900">{{ courier.fullName }}</h4>
              <p class="text-sm text-gray-500">{{ courier.phone }}</p>
            </div>
          </div>
        </div>

        <div class="p-4">
          <!-- Today's Assignment -->
          <div class="mb-4">
            <p class="text-sm text-gray-500 mb-1">{{ $t('Affectes aujourd\'hui') }}</p>
            <p class="text-xl font-semibold text-blue-600">{{ courier.todayAssigned || 0 }}</p>
          </div>

          <!-- Status Breakdown -->
          <div class="space-y-2">
            <div class="flex justify-between items-center">
              <span class="text-sm text-gray-500">{{ $t('En cours') }}</span>
              <span class="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                {{ courier.statusCounts ? (courier.statusCounts.shipped || 0) : 0 }}
              </span>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-sm text-gray-500">{{ $t('Livres') }}</span>
              <span class="px-2 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                {{ courier.statusCounts ? (courier.statusCounts.delivered || 0) : 0 }}
              </span>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-sm text-gray-500">{{ $t('Retours') }}</span>
              <span class="px-2 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                {{ courier.statusCounts ? (courier.statusCounts.return || 0) : 0 }}
              </span>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-sm text-gray-500">{{ $t('Refuses') }}</span>
              <span class="px-2 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium">
                {{ courier.statusCounts ? (courier.statusCounts.refused || 0) : 0 }}
              </span>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-sm text-gray-500">{{ $t('Annules') }}</span>
              <span class="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-medium">
                {{ courier.statusCounts ? (courier.statusCounts.cancelled || 0) : 0 }}
              </span>
            </div>
          </div>

          <!-- Total Active -->
          <div class="mt-4 pt-4 border-t">
            <div class="flex justify-between items-center">
              <span class="text-sm font-medium text-gray-700">{{ $t('Total actifs') }}</span>
              <span class="text-lg font-bold text-gray-900">{{ courier.totalActive || 0 }}</span>
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="px-4 py-3 bg-gray-50 border-t">
          <div class="flex gap-2">
            <a
              :href="'tel:' + courier.phone"
              class="flex-1 px-3 py-2 bg-green-600 text-white text-sm text-center rounded hover:bg-green-700"
            >
              <svg class="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
              </svg>
              {{ $t('Appeler') }}
            </a>
            <router-link
              :to="'/stockist-dashboard/assign?courier=' + courier._id"
              class="flex-1 px-3 py-2 bg-blue-600 text-white text-sm text-center rounded hover:bg-blue-700"
            >
              <svg class="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
              </svg>
              {{ $t('Affecter') }}
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MyCouriers',
  props: {
    currentUser: { type: Object },
    idWharhouse: { type: Object },
  },
  data() {
    return {
      loading: false,
      couriers: []
    };
  },
  async mounted() {
    if (this.currentUser && this.currentUser._id) {
      await this.loadCouriers();
    }
  },
  watch: {
    // Reload when warehouse changes in header
    idWharhouse: {
      handler: function() {
        this.loadCouriers();
      },
      deep: true
    }
  },
  methods: {
    async loadCouriers() {
      this.loading = true;
      try {
        const params = {};

        // Use selected country from header
        if (this.idWharhouse && this.idWharhouse.country) {
          params.country = this.idWharhouse.country;
        }

        const res = await this.$server.getMyCouriers('stockistDashboard', params);
        if (res && res.content && res.content.couriers) {
          this.couriers = res.content.couriers;
        } else if (res && res.couriers) {
          this.couriers = res.couriers;
        }
      } catch (error) {
        console.error('Error loading couriers:', error);
      }
      this.loading = false;
    },

    getInitials(name) {
      if (!name) return '?';
      const parts = name.split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    },

    getTotalDelivered() {
      return this.couriers.reduce(function(sum, c) {
        return sum + (c.statusCounts && c.statusCounts.delivered ? c.statusCounts.delivered : 0);
      }, 0);
    },

    getTotalShipped() {
      return this.couriers.reduce(function(sum, c) {
        return sum + (c.statusCounts && c.statusCounts.shipped ? c.statusCounts.shipped : 0);
      }, 0);
    }
  }
};
</script>

<style scoped>
</style>
